import axios from "axios";
import { authEndpoints } from "endpoints";
import { toast } from "react-toastify";

import { ENV } from "./constants/env";
import { handleAuthError } from "./hooks/useUserPermissionsSync";

// axios.defaults.baseURL = "http://localhost:5001/";
// axios.defaults.baseURL = "https://prod-api.elithair.tech";
// axios.defaults.baseURL = "https://staging-api.elithair.tech";

// default api url v1
axios.defaults.baseURL = ENV.ELITHAIR_API_URL.v1;

axios.defaults.headers["Access-Control-Allow-Origin"] = "*";

const serverErrorMessage = "Something went wrong, Please try again later.";

// Create axios instances with their base URLs
const defaultAxios = axios.create({
    baseURL: ENV.ELITHAIR_API_URL.v1,
    headers: {
        "Access-Control-Allow-Origin": "*",
    },
});

// Axios instance for V2 connection
export const axiosClient = axios.create({
    baseURL: ENV.ELITHAIR_API_URL.v2,
    headers: {
        "Access-Control-Allow-Origin": "*",
    },
});

// Request interceptor for all instances
const createRequestInterceptor = async (config) => {
    const token = localStorage.getItem("session");
    if (token) {
        const session = JSON.parse(token);
        if (session?.accessToken) {
            config.headers.Authorization = session.accessToken;
        }
    }
    return config;
};

// Common response interceptor for all instances
const responseInterceptor = (response) => response;

// TODO: refactor this
// Extract error message from response
const extractErrorMessage = (errorResponse) => {
    try {
        const { data } = errorResponse;

        // Check for error.message pattern
        if (data?.error?.message && typeof data.error.message === "string") {
            const errorText = data.error.message;

            // Extract BadRequestError message
            if (errorText.includes("BadRequestError:")) {
                const matches = errorText.match(/BadRequestError:\s*(.+)$/);
                return matches?.[1]?.trim() || errorText;
            }

            // Extract Unhandled error message
            if (errorText.includes("Unhandled error:")) {
                const nestedMatches = errorText.match(/Unhandled error:\s*BadRequestError:\s*(.+)$/);
                return nestedMatches?.[1]?.trim() || errorText.replace("Unhandled error:", "").trim();
            }

            return errorText;
        }

        // Check other common error patterns
        if (data?.message) return data.message;
        if (typeof data === "string") return data;
        if (data?.errors?.length > 0) return data.errors[0].message || data.errors[0];

        return serverErrorMessage;
    } catch (parseError) {
        console.error("Error parsing error message:", parseError);
        return "An error occurred during the operation";
    }
};

// Common error handler for all instances
const errorInterceptor = async (error, axiosInstance) => {
    const originalConfig = error.config;

    // Handle network errors
    if (!error.response) {
        toast.error("Network error occurred. Please check your connection.");
        return Promise.reject(error);
    }

    // Handle token refresh for 401/403 errors
    if ([401, 403].includes(error.response.status) && !originalConfig._retry) {
        originalConfig._retry = true;

        try {
            const token = localStorage.getItem("session");
            if (!token) throw new Error("No session found");

            const session = JSON.parse(token);
            const response = await defaultAxios.post(authEndpoints.refreshToken, {
                refreshToken: session.refreshToken,
            });

            if (response.data?.accessToken) {
                localStorage.setItem(
                    "session",
                    JSON.stringify({
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                    })
                );
                return axiosInstance(originalConfig);
            }

            throw new Error("No refresh token found");
        } catch (_error) {
            handleAuthError();
            return Promise.reject(_error);
        }
    }

    // Log error data for debugging
    // console.log("Error Response:", JSON.stringify(error.response.data, null, 2));

    // Extract and display error message
    const errorMessage = extractErrorMessage(error.response);
    toast.error(errorMessage);
    // console.log("Final error message:", errorMessage);

    return Promise.reject(error.response?.data || error);
};

// Apply interceptors to both axios instances
[defaultAxios, axiosClient].forEach((instance) => {
    instance.interceptors.request.use(createRequestInterceptor);
    instance.interceptors.response.use(responseInterceptor, (error) => errorInterceptor(error, instance));
});

export default defaultAxios;
